import {createSlice, PayloadAction} from '@reduxjs/toolkit'
import {setListOfValues} from 'modules/search/slice'
import {DEFAULT_SEO_ATTRIBUTES} from 'seo/seo-db/attributes'
import {DEFAULT_SEO_ACTIVE_LOCALES} from 'seo/seoLocales'
import {AppThunk} from 'store'
import {LocalizationConfig} from 'types/Localization'
import {MetaState} from 'types/State'
import {hitsToLov} from 'utils/algolia'

import {type LovHit, Page} from '@vio/core/src/analytics/types/Events'

import {getLocalizationConfig} from './getLocalizationConfig'
import {
  getCurrencyCode,
  getFallbackLanguages,
  getLanguageCode
} from './selectors'

import type {ClientOptions} from '@findhotel/sapi'
import type {DeviceCategory} from '@vio/core/src/utils/userAgent/types'

export const initialState: MetaState = {
  address: undefined,
  anonymousId: undefined,
  analyticsPageName: undefined,
  brandCode: undefined,
  subBrand: undefined,
  currencyCode: undefined,
  deviceCategory: undefined,
  deviceLayout: undefined,
  devicePlatform: undefined,
  fallbackLanguages: undefined,
  includeLocalTaxes: undefined,
  includeTaxes: undefined,
  isRTLLanguage: undefined,
  languageCode: undefined,
  localeCode: undefined,
  trafficSource: undefined,
  utmReferrer: undefined,
  userCountryCode: undefined,
  userCountryRegion: undefined,
  userMeasurementSystem: undefined,
  viewPortHeight: undefined,
  viewPortWidth: undefined,
  localizationIsInitialized: false,
  isAcquiringGeoCoordinates: false,
  showComparisonOffers: true,
  isReturningUser: false,
  firstVisit: '',
  userAgentDetails: undefined,
  userAgent: undefined,
  ghaClassification: null,
  deviceId: undefined,
  visitsCount: 0,
  currencyExchangeRate: null,
  currencyExchangeRateEur: null,
  isVioFlutter: false,
  userCity: '',
  seoActiveLocales: DEFAULT_SEO_ACTIVE_LOCALES,
  seoAttributes: DEFAULT_SEO_ATTRIBUTES
}

interface SetDeviceLayout {
  deviceCategory: DeviceCategory
  viewPortHeight: number
  viewPortWidth: number
}

interface setAnalyticsPageName {
  page: Page
}

const {actions, reducer} = createSlice({
  name: 'meta',
  initialState,
  reducers: {
    setDeviceLayout: (state, {payload}: PayloadAction<SetDeviceLayout>) => {
      state.deviceLayout = payload.deviceCategory
      state.viewPortHeight = payload.viewPortHeight
      state.viewPortWidth = payload.viewPortWidth
    },
    setAnalyticsPageName: (
      state,
      {payload}: PayloadAction<setAnalyticsPageName>
    ) => {
      state.analyticsPageName = payload.page
    },
    setIsAcquiringGeoCoordinates: (
      state,
      {payload}: PayloadAction<boolean>
    ) => {
      state.isAcquiringGeoCoordinates = payload
    },
    setLocalizationConfig: (
      state,
      {payload}: PayloadAction<Partial<LocalizationConfig>>
    ) => {
      return {
        localizationIsInitialized: true,
        ...state,
        ...payload
      }
    },
    setShowComparisonOffers: (
      state,
      {payload}: PayloadAction<{showComparisonOffers: boolean}>
    ) => ({
      ...state,
      showComparisonOffers: payload.showComparisonOffers
    }),
    setCurrencyExchangeRates: (
      state,
      {
        payload
      }: PayloadAction<{
        currencyExchangeRate: number
        currencyExchangeRateEur: number
      }>
    ) => {
      state.currencyExchangeRate = payload.currencyExchangeRate
      state.currencyExchangeRateEur = payload.currencyExchangeRateEur
    }
  }
})

export const initializeLocalizationConfig = (): AppThunk => async dispatch => {
  const localizationConfig = await getLocalizationConfig(dispatch)
  dispatch(actions.setLocalizationConfig(localizationConfig))
}

type Configs = Parameters<ClientOptions['callbacks']['onConfigReceived']>[0]
export const storeSapiConfig =
  (configs: Configs): AppThunk =>
  async (dispatch, getState) => {
    const state = getState()
    const currencyCode = getCurrencyCode(state)
    const language = getLanguageCode(state)
    const fallbackLanguages = getFallbackLanguages(state)

    const listOfValues = hitsToLov(configs.lov as LovHit[], [
      language,
      ...fallbackLanguages
    ])

    dispatch(setListOfValues({listOfValues}))

    dispatch(
      actions.setCurrencyExchangeRates({
        currencyExchangeRate: configs.exchangeRates?.[currencyCode],
        currencyExchangeRateEur:
          configs.exchangeRates?.[currencyCode] / configs.exchangeRates?.EUR
      })
    )
  }

export default reducer

export const {
  setDeviceLayout,
  setAnalyticsPageName,
  setLocalizationConfig,
  setIsAcquiringGeoCoordinates,
  setShowComparisonOffers
} = actions
